/*
 * Design: Gradient Noir – Large SaaS-style footer with dark bg and aquamarine accents
 * 4 columns: Services, Company, Contact, CTA
 */
import { Link } from "wouter";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const companyLinks = [
  { href: "/about", label: "אודות" },
  { href: "/clients", label: "לקוחות" },
  { href: "/projects", label: "פרויקטים" },
  { href: "/blog", label: "בלוג" },
  { href: "/contact", label: "צור קשר" },
];

const serviceLinks = [
  { href: "/services/web-development", label: "בניית אתרים" },
  { href: "/services/seo", label: "קידום אורגני" },
  { href: "/services/paid-advertising", label: "קידום ממומן" },
  { href: "/services/social-media", label: "ניהול סושיאל" },
  { href: "/services/ai-marketing", label: "קידום AI" },
];

export default function Footer() {
  return (
    <footer className="relative bg-[#080e1e] border-t border-white/5">
      {/* Top gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/50 to-transparent" />

      <div className="container py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Column 1 – Brand + Description */}
          <div>
            <div className="mb-6">
              <Link href="/">
                <img
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663396493078/EDyimBtawRo3FYhzWUwDs6/upsite_white_3261f55b.png"
                  alt="Upsite – אפסייט"
                  className="h-8 w-auto object-contain"
                />
              </Link>
            </div>
            <p className="text-sm text-white/50 leading-relaxed mb-5">
              אפסייט – בניית אתרים וקידום אורגני שמביא תוצאות. יותר מ-20 שנות ניסיון בדיגיטל.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/upsitegroup"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#66CDAA] hover:border-[#66CDAA]/30 transition-all"
                aria-label="Facebook"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a
                href="https://instagram.com/upsitegroup"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#66CDAA] hover:border-[#66CDAA]/30 transition-all"
                aria-label="Instagram"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a
                href="http://www.linkedin.com/company/1812809"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#66CDAA] hover:border-[#66CDAA]/30 transition-all"
                aria-label="LinkedIn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a
                href="https://twitter.com/upsitegroup"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#66CDAA] hover:border-[#66CDAA]/30 transition-all"
                aria-label="Twitter / X"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>

          {/* Column 2 – Services */}
          <div>
            <p className="text-xs font-semibold text-[#66CDAA] uppercase tracking-wider mb-4">
              שירותים
            </p>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/60 hover:text-[#66CDAA] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 – Company */}
          <div>
            <p className="text-xs font-semibold text-[#66CDAA] uppercase tracking-wider mb-4">
              חברה
            </p>
            <ul className="space-y-2">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/60 hover:text-[#66CDAA] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 – Contact */}
          <div>
            <p className="text-xs font-semibold text-[#66CDAA] uppercase tracking-wider mb-4">
              יצירת קשר
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#66CDAA] shrink-0" />
                <a href="tel:0542950091" className="text-sm text-white/60 hover:text-[#66CDAA] transition-colors" dir="ltr">
                  054-295-0091
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#66CDAA] shrink-0" />
                <a href="mailto:info@upsite.co.il" className="text-sm text-white/60 hover:text-[#66CDAA] transition-colors">
                  info@upsite.co.il
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#66CDAA] shrink-0 mt-0.5" />
                <span className="text-sm text-white/60">
                  כ״ג יורדי הסירה 1, תל אביב
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#66CDAA] shrink-0" />
                <span className="text-sm text-white/60">
                  א׳-ה׳ 09:00-18:00
                </span>
              </li>
            </ul>
            <Link
              href="/contact"
              className="glow-btn inline-block text-sm px-6 py-3"
            >
              דברו איתנו
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} אפסייט בע״מ – Upsite LTD. כל הזכויות שמורות.
          </p>
          <div className="flex items-center gap-6">
            <Link href="/privacy" className="text-xs text-white/50 hover:text-white/70 transition-colors">
              מדיניות פרטיות
            </Link>
            <Link href="/accessibility" className="text-xs text-white/50 hover:text-white/70 transition-colors">
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
